import React, { useEffect, useState } from "react";
import logo from "../assets/branding/logo.svg";
import { navigateTo } from "../router/Router";
import endInterviewIcon from "@/assets/interview/endinterview/endInterview.png";
import tickIcon from "@/assets/interview/endinterview/tickIcon.png";
import { getReportStatus } from "@/api/reportApi";

const InterviewHeader: React.FC = () => {
  return (
    <div className="w-full bg-white fixed top-0 left-0 right-0 z-50 shadow-sm border-b border-gray-100">
      <div className="flex flex-row items-center justify-between py-4 px-6 md:px-12 lg:px-24 max-w-[1920px] mx-auto">
        <div className="flex flex-row items-center gap-3 text-[#001630] font-ubuntu text-xl font-medium">
          <img src={logo} alt="logo" className="w-[160px] h-auto sm:w-[150px] md:w-[120px] lg:w-[140px]" />
        </div>
        <div className="flex flex-row items-center gap-4">
          <p className="text-gray-500 text-sm font-normal hidden sm:block tracking-wide">
            Facing a problem?
          </p>
          <button
            className="text-[#001630] text-center font-ubuntu text-sm font-medium tracking-wide underline decoration-solid underline-offset-4 uppercase hover:opacity-80 transition-opacity cursor-pointer"
            onClick={() => {}}
          >
            CONTACT US
          </button>
        </div>
      </div>
    </div>
  );
};

const EndInterviewIcon = () => {
  return (
    <div className="relative mb-6">
      <div className="bg-purple-200 rounded-full p-8 opacity-20 absolute inset-0 m-auto"></div>
      <div className="relative">
        <div className="flex items-center justify-center relative px-4 py-3">
          <img src={endInterviewIcon} alt="End Interview Icon" className="w-32 h-32" />
          <img src={tickIcon} alt="Tick Icon" className="w-14 h-14 absolute bottom-0 right-[-5%]" />
        </div>
      </div>
    </div>
  )
};

interface InterviewEndedProps {
  id?: string;
  [key: string]: any;
}

const InterviewEnded: React.FC<InterviewEndedProps> = ({ id }) => {
  const [isReportReady, setIsReportReady] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    const pollInterval = 3000;

    const checkReportStatus = async () => {
      try {
        const status = await getReportStatus(id);
        
        if (!isMounted) return;

        const isCoreCompleted = status.coreReport?.status === 'completed';
        const isCompleted = status.generationStatus === 'completed' || isCoreCompleted;

        if (isCompleted) {
          setIsReportReady(true);
          setReportId(id);
        } else if (status.generationStatus === 'failed') {
          //
        }
      } catch (error) {
        //
      }
    };

    checkReportStatus();

    const intervalId = setInterval(() => {
      if (!isReportReady) {
        checkReportStatus();
      }
    }, pollInterval);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [id, isReportReady]);

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans">
      {/* Header */}
      <InterviewHeader />

      {/* Body */}
      <div className="flex-1 flex items-center justify-center pt-[80px]">
        <div className="flex flex-col items-center justify-center h-full w-full p-4 relative">
          {/* Optional Grid Background if needed, using CSS for simplicity */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(#001630 1px, transparent 1px), linear-gradient(90deg, #001630 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          ></div>

          <div className="flex flex-col items-center max-w-md w-full text-center z-10">
            {/* Purple puzzle piece with checkmark */}
            <EndInterviewIcon />

            {/* Text content */}
            <h1 className="text-2xl md:text-[24px] font-ubuntu font-medium leading-tight text-[#001630] mb-3">
              Interview Completed
            </h1>
            <p className="text-gray-600 text-sm mb-8 leading-relaxed">
              Your interview report is currently being generated and
              <br className="hidden sm:block" />
              will be available in your profile shortly.
            </p>

            {/* Buttons */}
            <div className="flex flex-row items-center justify-center gap-4 w-full">
              <button
                className="bg-white text-[#001630] border border-[#001630] py-2 px-4 text-sm rounded hover:bg-gray-50 transition-colors font-medium min-w-[120px]"
                onClick={() => navigateTo("/")}
              >
                Go to Home
              </button>

              <button
                className="bg-[#001630] text-white border border-[#001630] py-2 px-4 text-sm rounded disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[160px] font-medium"
                disabled={!isReportReady}
                onClick={() => {
                  if (reportId) {
                    navigateTo(`/report/${reportId}`);
                  }
                }}
              >
                {!isReportReady ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating Report...</span>
                  </>
                ) : (
                  'View Report'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewEnded;
